import { Card, Stack, Typography, Avatar, Button, Chip, Link, IconButton, CircularProgress } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { User } from "../../contexts/UserContext";
import { AuthorizationStatus, FundingPackage, FundingPriority } from "../types";
import { PriorityStyles } from "../EditFunding/columns";
import axios from "axios";
import { ArrowForward } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { DocumentPreviewOverlay } from "./DocumentPreviewOverlay";

interface Props {
  fundingPackage: FundingPackage | undefined;
  refreshData: () => Promise<void>;
  disabled?: boolean;
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

export default function ReviewFundingPackageDetails(props: Props) {
  const style = PriorityStyles[props.fundingPackage?.priority || 4];
  const [openIframeDocId, setOpenIframeDocId] = useState<number | null>(null);
  const [trustAccountNumber, setTrustAccountNumber] = useState<string>("");
  const [accountLoading, setAccountLoading] = useState(false);
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  const FundingPriorityDisplayName: Record<FundingPriority, string> = {
    [FundingPriority.PayoffCutoff]: "Payoff Cutoff",
    [FundingPriority.Now]: "Now",
    [FundingPriority.Soon]: "Soon",
    [FundingPriority.Today]: "Today"
  };
  const getTrustAccountNumber = async () => {
    setAccountLoading(true);
    const { data } = await axios.get(
      `/proxy/api/ledgers/GetOrderAndLedgerBalance?orderNumber=${props.fundingPackage?.orderNumber}`
    );
    setTrustAccountNumber(data.trustAccount.accountNumber);
    setAccountLoading(false);
  };
  const submitApproved = async (pdfId: number) => {
    if (props.disabled) return;
    await axios.post("/api/FundingPackages/packages/UpdateSourcesAndUsesStatus", {
      SourcesAndUsesId: pdfId,
      status: AuthorizationStatus.Approved
    });
    setOpenIframeDocId(null);
    props.refreshData();
  };
  const submitRejected = async (pdfId: number) => {
    if (props.disabled) return;
    await axios.post("/api/FundingPackages/packages/UpdateSourcesAndUsesStatus", {
      SourcesAndUsesId: pdfId,
      status: AuthorizationStatus.Rejected
    });
    setOpenIframeDocId(null);
    await props.refreshData();
  };
  const maskAccountNumber = (accountNumber: string | number | null | undefined) => {
    if (!accountNumber) return "—";
    const str = String(accountNumber);
    if (str.length <= 4) return str;
    return `${"X".repeat(str.length - 4)}${str.slice(-4)}`;
  };
  useEffect(() => {
    if (props.fundingPackage?.orderNumber) {
      getTrustAccountNumber();
    }
  }, [props.fundingPackage]);

  return (
    <Stack>
      <Stack direction="row" spacing={2} >
        <Card sx={{ p: 2, maxWidth: 330, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "grey.400" }}>
              {getInitials(
                props.fundingPackage?.createdByUser?.fullName ? props.fundingPackage?.createdByUser?.fullName : ""
              )}
            </Avatar>

            <Stack flex={1}>
              <Typography fontWeight="bold">Submitted by</Typography>
              <Typography variant="body2" color="text.secondary">
                {props.fundingPackage?.createdByUser?.fullName}
              </Typography>
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<EmailIcon />}
              component="a"
              href={`mailto:${props.fundingPackage?.createdByEmail}?subject=${props.fundingPackage?.orderNumber} Funding Package ${props.fundingPackage?.packageNumber}`}
            >
              Email
            </Button>
          </Stack>
        </Card>
        <Card sx={{ p: 2, maxWidth: 330, flex: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight="bold">Priority</Typography>

            <Chip
              label={FundingPriorityDisplayName[props.fundingPackage?.priority || 4]}
              sx={{
                color: style?.color,
                borderColor: style?.border,
                backgroundColor: style?.bg,
                fontWeight: 500
              }}
            />
          </Stack>
        </Card>
      </Stack>
      <Stack direction="row" spacing={2} pt={6}>
        <Card sx={{ p: 2, maxWidth: 330, flex: 1, alignSelf: "flex-start" }}>
          <Typography fontWeight="bold">Trust Account</Typography>
          <Typography color="text.secondary">
            {accountLoading ? <CircularProgress /> : maskAccountNumber(trustAccountNumber)}
          </Typography>
        </Card>
        <Stack direction="column" spacing={2} sx={{ maxWidth: 330, flex: 1 }}>
          {props.fundingPackage?.sourcesAndUsesPdfs?.map((pdf) => (
            <Card key={pdf.id} sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight="bold">Funding Package</Typography>
                {pdf.authorizationStatus === 1 && (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForward />}
                    sx={{
                      backgroundColor: "blue",
                      color: "white",
                      textTransform: "none",
                      width: 80,
                      "&:hover": { backgroundColor: "blue", borderColor: "blue" }
                    }}
                    onClick={() => setOpenIframeDocId(pdf.id)}
                    disabled={props.disabled}
                  >
                    Review
                  </Button>
                )}
                {pdf.authorizationStatus === 2 && (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForward />}
                    sx={{
                      backgroundColor: "#4caf50",
                      color: "white",
                      textTransform: "none",
                      width: 95,
                      "&:hover": { backgroundColor: "#4caf50", borderColor: "#4caf50" }
                    }}
                    onClick={() => setOpenIframeDocId(pdf.id)}
                    disabled={props.disabled}
                  >
                    Approved
                  </Button>
                )}
                {pdf.authorizationStatus === 3 && (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForward />}
                    sx={{
                      backgroundColor: "#f44336",
                      color: "white",
                      textTransform: "none",
                      width: 90,
                      "&:hover": { backgroundColor: "#f44336", borderColor: "#f44336" }
                    }}
                    onClick={() => setOpenIframeDocId(pdf.id)}
                    disabled={props.disabled}
                  >
                    Deficient
                  </Button>
                )}
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Link target="_blank" underline="hover" href={`${momentumUrl}opendoc?id=${pdf.momentumDocumentId}`}>
                  {pdf.momentumDocumentName}
                </Link>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Stack>
      {openIframeDocId != null && (
        <DocumentPreviewOverlay
          open={true}
          momentumUrl={momentumUrl}
          documentId={
            props.fundingPackage?.sourcesAndUsesPdfs?.find((p) => p.id === openIframeDocId)?.momentumDocumentId
          }
          onClose={() => setOpenIframeDocId(null)}
          onApprove={() => submitApproved(openIframeDocId)}
          onReject={() => submitRejected(openIframeDocId)}
        />
      )}
    </Stack>
  );
}
